import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DraftOrderFilter, DraftStatus, OrderScope } from 'models/order';

type DraftOrdersPageActionsProps = {
    onFilterChange: (filters: DraftOrderFilter) => void;
};

const DraftOrdersPageActions: FC<DraftOrdersPageActionsProps> = ({ onFilterChange }: DraftOrdersPageActionsProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);

    const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout>>();
    const [search, setSearch] = useState('');
    const [scope, setScope] = useState<OrderScope>(OrderScope.MY_DRAFTS);
    const [status, setStatus] = useState<DraftStatus>(DraftStatus.ALL);

    const emitChange = (search: string, scope: OrderScope, status: DraftStatus) => {
        onFilterChange({
            search,
            scope,
            status: status !== DraftStatus.ALL ? status : undefined,
        });
    };

    const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearch(value);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        const timeout = setTimeout(() => {
            if (value.length === 0 || value.length >= 3) {
                emitChange(value, scope, status);
            }
        }, 300);
        setSearchTimeout(timeout);
    };

    const handleScopeChange = (event: SelectChangeEvent) => {
        const value = event.target.value as OrderScope;
        setScope(value);
        emitChange(search, value, status);
    };

    const handleStatusChange = (event: SelectChangeEvent) => {
        const value = event.target.value as DraftStatus;
        setStatus(value);
        emitChange(search, scope, value);
    };

    return (
        <Fragment>
            <Box mb={1} display="flex" alignItems="center">
                <Grid container spacing={1}>
                    <Grid item md={2} sm={6} xs={12}>
                        <TextField
                            fullWidth
                            id="filter-name"
                            onChange={handleTextChange}
                            margin="dense"
                            name="search"
                            label={translate('orders:ORDERS_FREE_TEXT_FILTER')}
                            placeholder={translate('orders:ORDERS_FREE_TEXT_FILTER_PLACEHOLDER')}
                        />
                    </Grid>
                    <Grid item md={2} sm={6} xs={12}>
                        <FormControl fullWidth margin="dense">
                            <InputLabel id="clinic-select-label">{translate('orders:DRAFTS')}</InputLabel>
                            <Select
                                value={scope?.toString()}
                                onChange={handleScopeChange}
                                labelId="clinic-select-label"
                                label={translate('orders:DRAFTS')}
                            >
                                <MenuItem value="MY_DRAFTS" selected>
                                    {translate('common:MY_DRAFTS')}
                                </MenuItem>
                                <MenuItem value="ALL_DRAFTS">{translate('common:ALL_DRAFTS')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item md={2} sm={6} xs={12}>
                        <FormControl fullWidth margin="dense">
                            <InputLabel id="status-select-label">{translate('common:STATUS')}</InputLabel>
                            <Select
                                value={status?.toString()}
                                onChange={handleStatusChange}
                                labelId="status-select-label"
                                label={translate('common:STATUS')}
                            >
                                <MenuItem value="ALL">{translate('common:ALL')}</MenuItem>
                                <MenuItem value="DRAFT">{translate('DRAFT')}</MenuItem>
                                <MenuItem value="COMPLETED_DRAFT">{translate('COMPLETED_DRAFT')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>
        </Fragment>
    );
};

export default DraftOrdersPageActions;
